import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";
import { BRAND, LAB, LAB_REPORT, PURITY_REPORT } from "@/content/brand";
import {
  AYURVEDA_HERITAGE, PURITY_EXPLAINED, NUTRITION_EXPLAINED, EXPLAINED_SOURCES,
} from "@/content/labExplained";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const PATH = "/coconut-oil-lab-report-explained";
const UPDATED = "2026-09-25";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const QUESTION = {
  br: "What is the butyro refractometer reading of coconut oil?",
  sap: "What is the saponification value of coconut oil?",
  iv: "What is the iodine value of coconut oil?",
  unsap: "What does unsaponifiable matter mean in coconut oil?",
  av: "What is a good acid value for coconut oil?",
  arg: "Why is coconut oil tested for argemone oil?",
};

/** Questions people search for, answered from the same data. */
const FAQS = [
  ...PURITY_EXPLAINED.map((x) => ({ q: QUESTION[x.key], a: `${x.what} ${x.science}` })),
  { q: "Is 89 g of saturated fat per 100 g normal for coconut oil?", a: NUTRITION_EXPLAINED.find((x) => x.key === "sfa").science },
];

function Row({ label, children, muted = false }) {
  return (
    <div className="grid sm:grid-cols-[150px_1fr] gap-1 sm:gap-6 py-4 border-t border-black/10">
      <dt className="font-body text-[13px] font-semibold text-brand-ink">{label}</dt>
      <dd className={`font-body text-[16px] leading-relaxed ${muted ? "text-ink-muted/80" : "text-ink-muted"}`}>{children}</dd>
    </div>
  );
}

function PurityCard({ x }) {
  return (
    <article id={x.key} className="scroll-mt-20 rounded-3xl bg-white p-7 sm:p-10" data-testid={`explained-${x.key}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h3 className="font-display text-[26px] sm:text-[30px] leading-tight tracking-heading text-brand-ink max-w-md">{x.name}</h3>
        <div className="text-right">
          <p className="font-display text-[40px] leading-none tracking-display text-brand-ink">{x.value}</p>
          <p className="mt-2 inline-flex rounded-control bg-brand-parchment-soft px-3 py-1 font-body text-[12px] text-brand-ink">
            FSSAI limit: {x.limit}
          </p>
        </div>
      </div>
      <dl className="mt-6">
        <Row label="What it is">{x.what}</Row>
        <Row label="Our result">{x.result}</Row>
        <Row label="Why it matters">{x.science}</Row>
        <Row label="In Ayurveda" muted={!x.ayurveda}>
          {x.ayurveda || "No traditional link. We'd rather say so than force one."}
        </Row>
      </dl>
    </article>
  );
}

function NutritionCard({ x }) {
  return (
    <article id={x.key} className="scroll-mt-20 rounded-3xl bg-brand-obsidian-soft p-7 flex flex-col" data-testid={`explained-${x.key}`}>
      <p className="font-body text-[13px] text-brand-bone/60">{x.name}</p>
      <p className="font-display text-white text-[34px] leading-none tracking-display mt-2">{x.value}</p>
      <p className="mt-4 font-body text-[14px] text-brand-bone/70">{x.what}</p>
      <p className="mt-3 font-body text-[15px] leading-relaxed text-brand-bone/85">{x.science}</p>
      {x.ayurveda && (
        <p className="mt-4 border-t border-white/10 pt-4 font-body text-[14px] leading-relaxed text-brand-bone/70">
          <span className="font-semibold text-brand-bone">In Ayurveda: </span>{x.ayurveda}
        </p>
      )}
    </article>
  );
}

export default function LabExplained() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Coconut oil lab report, explained",
      description: "Every parameter on Karijeeva's coconut oil lab reports explained in plain words, with what modern science says and where Ayurveda has a genuine view.",
      datePublished: UPDATED,
      dateModified: UPDATED,
      mainEntityOfPage: `${SITE_URL}${PATH}`,
      author: { "@type": "Organization", name: BRAND.name, url: SITE_URL },
      publisher: { "@type": "Organization", name: BRAND.company, url: SITE_URL },
      citation: [...EXPLAINED_SOURCES, ...AYURVEDA_HERITAGE.sources].map((s) => s.url),
    },
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Lab reports", path: "/lab-reports" }, { name: "Explained", path: PATH }]),
    faqLd(FAQS.map((f) => ({ question: f.q, answer: f.a }))),
  ];

  return (
    <>
      <Seo
        title="Coconut Oil Lab Report, Explained"
        description="What acid value, iodine value, saponification value and every other coconut oil test means, what our results show, and what science and Ayurveda say."
        type="article"
        url={`${SITE_URL}${PATH}`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-obsidian text-brand-bone" data-testid="page-lab-explained">
        <div className="max-w-5xl mx-auto px-6 pt-16 sm:pt-24 pb-20">
          <nav aria-label="Breadcrumb" className="font-body text-[13px] text-brand-bone/60">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <Link to="/lab-reports" className="hover:underline">Lab reports</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span>Explained</span>
          </nav>
          <h1 className="mt-6 font-display text-white text-[clamp(2.6rem,7vw,5rem)] leading-[0.98] tracking-display">
            Every number on our lab report. Explained.
          </h1>
          <p className="mt-6 max-w-3xl font-body text-[18px] sm:text-[20px] leading-relaxed text-brand-bone/75">
            Sixteen results from two reports by {LAB.name}, {LAB.city}. For each one: what it is, what ours shows, what modern science says it means for you, and, where there is a genuine connection, how Ayurveda sees it.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-3xl font-body text-[14px]">
            <a href="#purity" className="rounded-2xl bg-brand-obsidian-soft p-5 hover:bg-white/10">
              <span className="block text-white font-semibold">6 purity tests</span>
              <span className="text-brand-bone/70">Report {PURITY_REPORT.ref}, {fmtDate(PURITY_REPORT.issued)}. Met the FSSAI standard on every one.</span>
            </a>
            <a href="#nutrition" className="rounded-2xl bg-brand-obsidian-soft p-5 hover:bg-white/10">
              <span className="block text-white font-semibold">10 nutrition values</span>
              <span className="text-brand-bone/70">Report {LAB_REPORT.ref}, per 100 g of oil.</span>
            </a>
          </div>
        </div>
      </section>

      {/* Short answer */}
      <section className="bg-brand-parchment">
        <div className="max-w-3xl mx-auto px-6 pt-16">
          <div className="rounded-3xl bg-brand-parchment-soft p-7" data-testid="explained-tldr">
            <p className="font-body text-[13px] font-semibold text-brand-gold">The short answer</p>
            <p className="mt-2 font-body text-[18px] leading-relaxed text-brand-ink">
              The six purity tests check that the oil is coconut oil and nothing else, and our sample met the FSSAI standard for coconut oil on all six. They are identity checks, not health benefits. The nutrition values show a very saturated oil with almost no polyunsaturated or trans fat, which is why coconut oil is stable in the kitchen, and also why it is best used in sensible amounts alongside other oils.
            </p>
          </div>
        </div>
      </section>

      {/* Purity */}
      <section id="purity" className="bg-brand-parchment py-20 scroll-mt-16">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-body text-[15px] font-semibold text-brand-gold">Purity</p>
          <h2 className="mt-2 font-display text-[clamp(2rem,5vw,3.5rem)] leading-[1.04] tracking-display text-brand-ink max-w-3xl">
            Is it really coconut oil? Six tests answer that.
          </h2>
          <p className="mt-5 max-w-3xl font-body text-[18px] leading-relaxed text-ink-muted">
            These are the tests in the FSSAI standard for coconut oil ({PURITY_REPORT.standard.replace("FSSAI standard for coconut oil ", "")}). Each one would move if cheaper oils or mineral oil were mixed in. No single test catches everything, which is why they are read together.
          </p>
          <div className="mt-12 space-y-4">
            {PURITY_EXPLAINED.map((x) => <PurityCard key={x.key} x={x} />)}
          </div>
        </div>
      </section>

      {/* Nutrition */}
      <section id="nutrition" className="bg-brand-obsidian text-brand-bone py-24 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <p className="font-body text-[15px] font-semibold text-brand-bone/70">Nutrition, per 100 g</p>
          <h2 className="mt-2 font-display text-white text-[clamp(2rem,5vw,3.5rem)] leading-[1.04] tracking-display max-w-3xl">
            What's inside, and what it means for your kitchen.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {NUTRITION_EXPLAINED.map((x) => <NutritionCard key={x.key} x={x} />)}
          </div>
        </div>
      </section>

      {/* Ayurveda heritage */}
      <section id="ayurveda" className="bg-brand-parchment-soft py-24 scroll-mt-16" data-testid="explained-ayurveda">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1.2fr_1fr] gap-12">
          <div>
            <p className="font-body text-[15px] font-semibold text-brand-gold">Heritage</p>
            <h2 className="mt-2 font-display text-[clamp(2rem,5vw,3.25rem)] leading-[1.04] tracking-display text-brand-ink">
              Narikela taila: coconut oil in Ayurveda.
            </h2>
            <p className="mt-6 font-body text-[17px] leading-relaxed text-ink-muted">{AYURVEDA_HERITAGE.intro}</p>
            <p className="mt-4 font-body text-[17px] leading-relaxed text-ink-muted">
              One honest detail: for daily oil pulling (gandusha), the classical texts usually name sesame oil. Using coconut oil for it is a newer habit, and a popular one.
            </p>
            <p className="mt-6 rounded-2xl bg-white p-5 font-body text-[15px] leading-relaxed text-brand-ink">{AYURVEDA_HERITAGE.note}</p>
            <ol className="mt-6 list-decimal pl-5 space-y-1 font-body text-[14px] text-ink-muted">
              {AYURVEDA_HERITAGE.sources.map((s) => (
                <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline break-words">{s.label}</a></li>
              ))}
            </ol>
          </div>
          <div className="rounded-3xl bg-white p-7 self-start">
            <p className="font-display text-[20px] text-brand-ink">A short glossary</p>
            <dl className="mt-4 divide-y divide-black/10">
              {AYURVEDA_HERITAGE.terms.map(([t, d]) => (
                <div key={t} className="py-3 grid grid-cols-[130px_1fr] gap-4">
                  <dt className="font-body text-[14px] font-semibold text-brand-ink">{t}</dt>
                  <dd className="font-body text-[14px] text-ink-muted">{d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* FAQ + sources + CTA */}
      <section className="bg-brand-parchment py-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-[32px] tracking-heading text-brand-ink">Questions people ask</h2>
          <div className="mt-6 divide-y divide-black/10 border-y border-black/10">
            {FAQS.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6 font-display text-[18px] text-brand-ink">
                  {f.q}
                  <span className="font-body text-2xl leading-none text-ink-muted transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 font-body text-[16px] leading-relaxed text-ink-muted">{f.a}</p>
              </details>
            ))}
          </div>

          <h2 className="mt-16 font-display text-[20px] text-brand-ink">Sources</h2>
          <ol className="mt-3 list-decimal pl-5 space-y-1 font-body text-[14px] text-ink-muted">
            {EXPLAINED_SOURCES.map((s) => (
              <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:underline break-words">{s.label}</a></li>
            ))}
          </ol>

          <div className="mt-16 rounded-3xl bg-brand-obsidian text-brand-bone p-8 sm:p-10">
            <p className="font-display text-white text-[28px] leading-tight tracking-heading">Want the report for your own jar?</p>
            <p className="mt-3 font-body text-brand-bone/75 leading-relaxed">
              Every label carries a batch number. Write to {BRAND.support.email} or call {BRAND.support.phone} with it, and we'll send you that batch's report.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              <Link to="/products" className="rounded-control bg-brand-gold hover:bg-brand-gold-deep px-6 py-3 font-body text-[15px] text-white">Shop Karijeeva</Link>
              <Link to="/lab-reports" className="inline-flex items-center gap-1 py-3 font-body text-[15px] text-white hover:underline">Back to lab reports <ChevronRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
